import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SWOTGridProps {
  swot: Record<string, unknown>;
}

/**
 * SWOT quadrants in the design prototype's shape: a 2×2 card grid where each
 * list row carries a small colored bullet dot (strengths green, weaknesses
 * and threats red, opportunities amber) and a hairline divider — no emoji.
 */
const SECTIONS: { key: string; title: string; dot: string }[] = [
  { key: 'strengths', title: 'Strengths', dot: 'bg-success' },
  { key: 'weaknesses', title: 'Weaknesses', dot: 'bg-destructive' },
  { key: 'opportunities', title: 'Opportunities', dot: 'bg-warning' },
  { key: 'threats', title: 'Threats', dot: 'bg-destructive' },
];

function getItems(swot: Record<string, unknown>, key: string): string[] {
  // Try direct: swot.strengths
  let items = swot[key];
  // Try nested: swot.swot.strengths
  if (!Array.isArray(items) && swot.swot && typeof swot.swot === 'object') {
    items = (swot.swot as Record<string, unknown>)[key];
  }
  if (!Array.isArray(items)) return [];
  return (items as Array<unknown>).map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return String((item as Record<string, unknown>).text || '');
    return '';
  }).filter(Boolean);
}

export default function SWOTGrid({ swot }: SWOTGridProps) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {SECTIONS.map((sec) => {
        const items = getItems(swot, sec.key);
        return (
          <Card key={sec.key}>
            <CardContent className="px-5 py-1">
              <h3 className="mb-1.5 text-sm font-bold tracking-[-0.01em] text-foreground">{sec.title}</h3>
              <ul className="m-0 list-none p-0">
                {items.length > 0 ? items.map((text, i) => (
                  <li
                    key={i}
                    className="relative border-b border-border py-[11px] pl-3.5 text-[13px] leading-relaxed text-foreground/80 last:border-b-0 last:pb-0 text-pretty"
                  >
                    <span className={cn('absolute left-0 top-[19px] size-[5px] rounded-full', sec.dot)} />
                    {text}
                  </li>
                )) : (
                  <li className="py-2 text-sm text-muted-foreground">No data available</li>
                )}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
